import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import background from "./images/background.png";
import PlatformToggle from "./atoms/PlatformToggle";
import Accordion from "./atoms/Accordion";

const getCountryInfo = (code) => {
  const normalized = code?.toUpperCase();
  if (normalized === "MX" || normalized === "MEXICO")
    return { label: "México", emoji: "🇲🇽" };
  if (
    normalized === "US" ||
    normalized === "USA" ||
    normalized === "ESTADOS_UNIDOS"
  )
    return { label: "Estados Unidos", emoji: "🇺🇸" };
  return null;
};

// Fast VPN Super (la que usa Mely) no tiene versión de Android confirmada,
// así que las listas de iPhone y Android no son un espejo exacto — cada una
// trae las apps que sí están verificadas para esa plataforma.
const IOS_APPS = [
  {
    name: "Proton VPN",
    emoji: "🛡️",
    url: "https://apps.apple.com/us/app/proton-vpn-fast-secure/id1437005085",
  },
  {
    name: "TunnelBear",
    emoji: "🐻",
    url: "https://apps.apple.com/us/app/tunnelbear-secure-vpn-wifi/id564842283",
  },
  {
    name: "Fast VPN Super",
    emoji: "🔑",
    recommended: true,
    url: "https://apps.apple.com/us/app/vpn-fast-vpn-super/id1528940523",
  },
  {
    name: "VPN Super Unlimited",
    emoji: "⚡",
    url: "https://apps.apple.com/us/app/vpn-super-unlimited-proxy/id1370293473",
  },
];

const ANDROID_APPS = [
  {
    name: "Proton VPN",
    emoji: "🛡️",
    url: "https://play.google.com/store/apps/details?id=ch.protonvpn.android",
  },
  {
    name: "TunnelBear",
    emoji: "🐻",
    url: "https://play.google.com/store/apps/details?id=com.tunnelbear.android",
  },
  {
    name: "VPN Super Unlimited",
    emoji: "⚡",
    url: "https://play.google.com/store/apps/details?id=com.free.vpn.super.hotspot.open",
  },
  {
    name: "Secure VPN",
    emoji: "🔒",
    url: "https://play.google.com/store/apps/details?id=com.fast.free.unblock.secure.vpn",
  },
];

const FAQ_ITEMS = [
  {
    q: "¿Tengo que pagar?",
    a: "Muchas VPNs son gratis para algunos países, pero pueden pedir un pago chiquito para otros (por ejemplo, conectarte a México puede costar centavos por semana en algunas apps). No necesitas una suscripción permanente — actívalo solo cuando haya tendencia activa.",
  },
  {
    q: "¿Qué país selecciono?",
    a: "El que indique la tendencia activa — revisa la página de Tendencia para saber cuál es.",
  },
  {
    q: "¿Es seguro?",
    a: "Sí. Un VPN solo cambia tu ubicación aparente, no le da acceso a nadie a tu información.",
  },
  {
    q: "¿Puedo usarlo desde mi computadora?",
    a: "Sí, la mayoría de estas apps también tienen versión de escritorio o extensión de navegador.",
  },
  {
    q: "¿Necesito crear una cuenta?",
    a: "Depende de la app — algunas te lo piden, otras no. Es normal, no tienes que pagar por eso.",
  },
];

const VpnGuide = () => {
  const [platform, setPlatform] = useState("iphone");
  const [searchParams] = useSearchParams();
  const country = getCountryInfo(searchParams.get("country"));
  const city = searchParams.get("city");

  useEffect(() => {
    const ua = navigator.userAgent;
    if (/android/i.test(ua)) setPlatform("android");
    else if (/iPhone|iPad|iPod/.test(ua)) setPlatform("iphone");
  }, []);

  return (
    <div
      className="min-h-full px-6 pt-10 pb-16 font-quicksand"
      style={{
        backgroundImage: `url(${background})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-2 px-4">
          <div className="text-4xl">🌎🔒</div>
          <h1 className="text-xl font-extrabold text-brand-primary">
            Guía rápida para usar un VPN
          </h1>
          <p className="text-sm text-gray-500">
            ¿Nunca has usado un VPN? No pasa nada. En menos de 2 minutos te
            enseñamos cómo hacerlo.
          </p>

          <p className="text-xs font-semibold inline-block px-3 py-1.5 rounded-full bg-violet-300/[0.15] text-brand-muted">
            💡 Solo úsalo mientras participas. Después puedes apagarlo.
          </p>
        </div>

        {/* Accordions */}
        <div className="space-y-3">
          <Accordion icon="🤔" title="¿Por qué necesito uno?">
            <p>
              {country
                ? `Esta tendencia es para ${country.label} ${country.emoji}. Al conectarte a ese país con un VPN, X puede detectar tu participación desde esa región, ayudando a que tu apoyo cuente para esa tendencia.`
                : "La tendencia activa está enfocada en un país específico. Al conectarte a ese país con un VPN, X puede detectar tu participación desde esa región, ayudando a que tu apoyo cuente."}
            </p>
            <p className="mt-2">
              No te preocupes, solo toma un par de minutos configurarlo. 💖
            </p>
          </Accordion>

          <Accordion icon="📱" title="Elige una VPN" defaultOpen>
            <div className="space-y-4">
              <div className="text-center">
                <p className="font-bold text-gray-500">
                  ¿Ya tienes una VPN instalada? ¡Perfecto! Puedes usar esa
                  misma.
                </p>
                <p>
                  Si todavía no tienes una, aquí te dejamos algunas opciones
                  populares para empezar.
                </p>
              </div>

              <PlatformToggle platform={platform} onChange={setPlatform} />

              <div className="space-y-3">
                {(platform === "iphone" ? IOS_APPS : ANDROID_APPS).map(
                  (app) => (
                    <div
                      key={app.name}
                      className="flex items-center justify-between gap-3 rounded-xl px-4 py-3 bg-violet-300/[0.12] border border-violet-300"
                    >
                      <span className="font-semibold text-sm text-brand-dark">
                        {app.emoji} {app.name}
                      </span>
                      <a
                        href={app.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-1.5 rounded-full font-bold text-xs active:scale-95 transition-transform flex-shrink-0 bg-violet-300/[0.15] text-brand-muted border border-violet-300"
                      >
                        Descargar
                      </a>
                    </div>
                  )
                )}
              </div>

              <div className="rounded-2xl p-4 text-sm text-left bg-violet-300/[0.12] border border-violet-300 text-gray-500">
                💡 Muchas VPNs ofrecen una versión gratuita o de prueba — revisa
                las opciones dentro de la app antes de descargar.
              </div>
            </div>
          </Accordion>

          <Accordion icon="🚀" title="Cómo conectarte" defaultOpen>
            <ol className="list-decimal list-inside space-y-2">
              <li>
                Si ya tienes una VPN instalada, ábrela. Si no, descarga una de
                las opciones de arriba{" "}
                {platform === "iphone"
                  ? "desde el App Store"
                  : "desde Play Store"}
                .
              </li>
              <li>Ábrelo.</li>
              <li>
                Selecciona el país de la tendencia{" "}
                {country ? (
                  <>
                    (
                    <strong className="text-brand-dark">
                      {country.label} {country.emoji}
                    </strong>
                    )
                  </>
                ) : (
                  <>
                    (por ejemplo{" "}
                    <strong className="text-brand-dark">
                      United States 🇺🇸
                    </strong>
                    )
                  </>
                )}
                .
                {city && (
                  <>
                    {" "}
                    Si tu VPN permite elegir una ciudad, usa{" "}
                    <strong className="text-brand-dark">📍 {city}</strong>.
                  </>
                )}
              </li>
              <li>
                Presiona <strong className="text-brand-dark">Conectar (Connect)</strong>.
              </li>
              <li>Abre X (Twitter) y apoya la tendencia.</li>
            </ol>
          </Accordion>

          <Accordion icon="🌎" title="¿Qué es un VPN?">
            <p>
              Un VPN hace que algunas aplicaciones piensen que estás conectado
              desde otro país.
            </p>
            <p className="mt-2">
              Conéctate al país que indique la tendencia activa (lo puedes ver
              en la página de <strong className="text-brand-dark">Tendencia</strong>).
            </p>
          </Accordion>

          <Accordion icon="❓" title="Preguntas frecuentes">
            <div>
              {FAQ_ITEMS.map((item) => (
                <Accordion key={item.q} title={item.q} variant="plain">
                  {item.a}
                </Accordion>
              ))}
            </div>
          </Accordion>
        </div>

        <div className="text-center">
          <a
            href="/tendencia"
            className="text-sm font-semibold underline text-brand-primary"
          >
            ✅ ¡Listo! Regresar a la tendencia
          </a>
        </div>
      </div>
    </div>
  );
};

export default VpnGuide;
